/**
 * Universal PDP - Skio Integration
 * Handles Skio selling plan integration and dynamic plan discovery
 */

// Global cache for discovered plans
window.SkioPlansCache = {
  plansByQuantity: {},
  variantPlans: {},
  allPlans: [],
  initialized: false
};

// Dynamic init is triggered from waitForSkioToLoad() in universal-pdp-pricing.js
// (after Skio product data is available). Do not run on DOMContentLoaded — too early.

document.addEventListener('skioPlanChanged', function() {
  initializeSkioDynamicPlans();
});

document.addEventListener('skioVariantChanged', function() {
  initializeSkioDynamicPlans();
});

/**
 * Initialize Dynamic Skio Plan Discovery
 * Discovers ALL available selling plans dynamically without hardcoded matching
 */
function initializeSkioDynamicPlans() {
  console.log('🔧 initializeSkioDynamicPlans called');
  
  const skioIntegration = document.querySelector('.pdp-details__skio-integration');
  if (!skioIntegration) {
    console.warn('⚠️ No .pdp-details__skio-integration found');
    return;
  }

  const skioPlanPicker = skioIntegration.querySelector('skio-plan-picker');
  if (!skioPlanPicker) {
    console.warn('⚠️ No skio-plan-picker found');
    return;
  }

  try {
    const productAttr = skioPlanPicker.getAttribute('product');
    if (!productAttr) {
      console.warn('⚠️ Skio component has no product attribute');
      return;
    }

    const productData = JSON.parse(productAttr);
    if (!productData || !productData.variants || !productData.selling_plan_groups) {
      console.warn('⚠️ Invalid product data structure');
      return;
    }

    console.log('📦 Product data loaded:', {
      variants: productData.variants.length,
      groups: productData.selling_plan_groups.length
    });

    // Discover all available Skio plans dynamically
    const discoveredPlans = discoverAvailablePlans(productData);
    
    const quantityKeys = Object.keys(discoveredPlans.plansByQuantity);
    if (quantityKeys.length === 0) {
      console.warn('No Skio plans mapped to quantities — keeping Liquid-rendered dropdown');
      window.SkioPlansCache.initialized = false;
      return;
    }

    console.log('Plans discovered:', {
      quantities: quantityKeys,
      totalPlans: discoveredPlans.allPlans.length
    });
    
    // Cache the discovered plans globally
    window.SkioPlansCache = {
      plansByQuantity: discoveredPlans.plansByQuantity,
      variantPlans: discoveredPlans.variantPlans,
      allPlans: discoveredPlans.allPlans,
      initialized: true
    };

    // Update the UI with dynamic data
    updateDeliveryDropdownDynamic(discoveredPlans);
    updatePricingDataDynamic(discoveredPlans);
    
    // Dispatch event to notify other modules
    document.dispatchEvent(new CustomEvent('skioDynamicPlansUpdated', {
      detail: { plans: window.SkioPlansCache }
    }));
    
    console.log('✅ Dynamic Skio Plans Initialized:', window.SkioPlansCache);
    
  } catch (error) {
    console.error('❌ Error initializing dynamic Skio plans:', error);
    // Fallback to existing static system if dynamic fails
    if (!window.SkioPlansCache) {
      window.SkioPlansCache = {};
    }
    window.SkioPlansCache.initialized = false;
    
    // Dispatch fallback event
    document.dispatchEvent(new CustomEvent('skioDynamicPlansFallback', {
      detail: { error: error.message }
    }));
  }
}

/**
 * Discover All Available Selling Plans Dynamically
 * This replaces all hardcoded plan detection logic
 */
function discoverAvailablePlans(productData) {
  const skioGroups = productData.selling_plan_groups.filter(group => group.app_id === 'SKIO');
  const allPlans = [];
  const plansByQuantity = {};
  const variantPlans = {};

  // Extract all Skio plans
  skioGroups.forEach(group => {
    group.selling_plans.forEach(plan => {
      allPlans.push({
        id: plan.id,
        name: plan.name,
        description: plan.description || '',
        options: plan.options || [],
        price_adjustments: plan.price_adjustments || [],
        group: group.name || 'Default Group'
      });
    });
  });

  // For each variant, find which plans are available
  productData.variants.forEach(variant => {
    const quantity = detectQuantityFromVariant(variant);
    
    // Get available selling plan allocations for this variant
    const allocations = variant.selling_plan_allocations || [];
    
    allocations.forEach(allocation => {
      const planId = allocation.selling_plan_id;
      const plan = allPlans.find(p => p.id === planId);
      
      if (plan) {
        // Store plan by quantity for easy lookup
        if (!plansByQuantity[quantity]) {
          plansByQuantity[quantity] = [];
        }
        
        // Use variant.compare_at_price (Shopify original price) as the strike-through
        // so subscription shows the same base price as one-time purchase
        const strikeCompareAtPrice = variant.compare_at_price || allocation.compare_at_price || variant.price;

        // Avoid duplicates
        if (!plansByQuantity[quantity].find(p => p.id === plan.id)) {
          plansByQuantity[quantity].push({
            ...plan,
            allocation: allocation,
            variant: variant,
            quantity: quantity,
            finalPrice: allocation.price,
            compareAtPrice: strikeCompareAtPrice,
            discountPercentage: calculateDiscountPercentage(allocation.price, strikeCompareAtPrice)
          });
        }

        // Store variant-specific plan data
        if (!variantPlans[variant.id]) {
          variantPlans[variant.id] = [];
        }
        variantPlans[variant.id].push({
          ...plan,
          allocation: allocation,
          quantity: quantity,
          finalPrice: allocation.price,
          compareAtPrice: strikeCompareAtPrice
        });
      }
    });
  });

  return {
    allPlans,
    plansByQuantity,
    variantPlans
  };
}

/**
 * Smart Quantity Detection from Variant
 * Handles multiple naming conventions automatically
 */
function detectQuantityFromVariant(variant) {
  const title = variant.title.toLowerCase();
  const option1 = (variant.option1 || '').toString().toLowerCase();
  const option2 = (variant.option2 || '').toString().toLowerCase();
  const option3 = (variant.option3 || '').toString().toLowerCase();
  
  // Try exact matches first
  for (let qty of ['1', '3', '6']) {
    if (option1 === qty || option2 === qty || option3 === qty) {
      return qty;
    }
  }
  
  // Try pattern matches
  const patterns = {
    '1': [/\b1\s*(month|bottle|tub)\b/, /\b1-pack\b/, /monthly/],
    '3': [/\b3\s*(month|bottle|tub)s?\b/, /\b3-pack\b/, /quarterly/],
    '6': [/\b6\s*(month|bottle|tub)s?\b/, /\b6-pack\b/, /semi-?annual/]
  };
  
  for (let qty in patterns) {
    for (let pattern of patterns[qty]) {
      if (pattern.test(title) || pattern.test(option1) || pattern.test(option2) || pattern.test(option3)) {
        return qty;
      }
    }
  }
  
  // Fallback based on common patterns
  if (title.includes('1') && !title.includes('3') && !title.includes('6')) return '1';
  if (title.includes('3')) return '3';
  if (title.includes('6')) return '6';
  
  // Default fallback
  return '3';
}

/**
 * Calculate Discount Percentage
 */
function calculateDiscountPercentage(salePrice, compareAtPrice) {
  if (!compareAtPrice || compareAtPrice <= salePrice) return 0;
  return Math.round(((compareAtPrice - salePrice) / compareAtPrice) * 100);
}

/**
 * Update Delivery Dropdown with Dynamic Plan Data
 */
function updateDeliveryDropdownDynamic(discoveredPlans) {
  try {
    const dropdown = document.querySelector('.pdp-details__delivery-dropdown');
    if (!dropdown) {
      console.warn('⚠️ Delivery dropdown not found, skipping dynamic update');
      return;
    }
    
    // If dropdown already has options from Liquid template, don't regenerate
    const existingOptions = dropdown.querySelectorAll('.pdp-details__delivery-option');
    if (existingOptions.length > 0) {
      console.log('✅ Dropdown already populated by Liquid template, skipping JS regeneration');
      return;
    }
    
    if (!discoveredPlans.plansByQuantity) {
      console.warn('⚠️ No plans by quantity found');
      return;
    }

    // Clear existing options only when we have replacements
    dropdown.innerHTML = '';

    // Create options for each available quantity plan
    const quantities = ['3', '1', '6']; // Order preference: most popular first
    let hasActiveOption = false;
    let optionsCreated = 0;

    quantities.forEach((qty, index) => {
      const plans = discoveredPlans.plansByQuantity[qty];
      if (!plans || plans.length === 0) {
        console.log(`ℹ️ No plans found for quantity ${qty}`);
        return;
      }

      // Find the plan whose delivery interval matches the quantity
      // Check plan.options for delivery_interval
      let plan = plans.find(p => {
        const options = p.options || [];
        for (const option of options) {
          if (option.name && option.name.toLowerCase().includes('delivery')) {
            const value = option.value ? option.value.toLowerCase() : '';
            // Match delivery interval to quantity (1 month, 3 months, 6 months)
            if ((qty === '1' && value.includes('1')) ||
                (qty === '3' && value.includes('3')) ||
                (qty === '6' && value.includes('6'))) {
              return true;
            }
          }
        }
        return false;
      });
      
      // If no match found by interval, fallback to first plan
      if (!plan) {
        plan = plans[0];
        console.warn(`⚠️ Could not match plan by interval for quantity ${qty}, using first plan`);
      }
      
      const isActive = !hasActiveOption; // First available plan becomes active
      if (isActive) hasActiveOption = true;

      const badge = qty === '3' ? 'most popular' : qty === '6' ? 'best value' : '';
      const description = `${qty} Bottle${qty !== '1' ? 's' : ''} - ${qty === '1' ? '30' : qty === '3' ? '90' : '180'} Day Supply`;
      
      // Just use the actual plan name from SKIO - no detection logic needed
      const displayName = plan.name;

      const optionEl = document.createElement('div');
      optionEl.className = `pdp-details__delivery-option${isActive ? ' pdp-details__delivery-option--active' : ''}`;
      optionEl.setAttribute('data-plan-id', plan.id);
      optionEl.setAttribute('data-frequency', `${qty}-month${qty !== '1' ? 's' : ''}`);
      optionEl.setAttribute('data-plan-name', displayName);
      optionEl.setAttribute('data-price', formatPriceCents(plan.finalPrice));
      optionEl.setAttribute('data-badge', badge);
      optionEl.setAttribute('data-quantity', qty);
      const policyPct = (plan.price_adjustments || []).find((adj) => adj.value_type === 'percentage');
      optionEl.setAttribute('data-subscribe-policy-pct', policyPct ? Number(policyPct.value) : 0);

      optionEl.innerHTML = `
        <div class="pdp-details__delivery-option-content">
          <span class="pdp-details__delivery-option-name">${displayName}</span>
          <span class="pdp-details__delivery-option-description">${description}</span>
          ${badge ? `<div class="pdp-details__delivery-option-badge">${badge}</div>` : ''}
        </div>
        <div class="pdp-details__delivery-option-price">${formatPriceCents(plan.finalPrice)}</div>
      `;

      dropdown.appendChild(optionEl);
      optionsCreated++;
    });

    console.log(`✅ Created ${optionsCreated} delivery options dynamically`);

    if (typeof window.updateSubscribeBenefitDiscountText === 'function') {
      window.updateSubscribeBenefitDiscountText();
    }
    
  } catch (error) {
    console.error('❌ Error updating delivery dropdown:', error);
  }
}

/**
 * Update Pricing Data Elements with Dynamic Values
 */
function updatePricingDataDynamic(discoveredPlans) {
  // Update subscription pricing in the UI
  const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
  const subscriptionOriginalPrice = document.querySelector('#subscriptionOriginalPrice');
  
  // Get the default active plan (3-month if available)
  const defaultPlans = discoveredPlans.plansByQuantity['3'] || discoveredPlans.plansByQuantity['1'] || [];
  if (defaultPlans.length > 0 && subscriptionCurrentPrice) {
    const defaultPlan = defaultPlans[0];
    subscriptionCurrentPrice.textContent = formatPriceCents(defaultPlan.finalPrice);
    
    if (subscriptionOriginalPrice) {
      subscriptionOriginalPrice.textContent = formatPriceCents(defaultPlan.compareAtPrice);
    }
  }

  // Update discount badges dynamically
  updateDynamicDiscountBadges(discoveredPlans);
}

/**
 * Update Discount Badges with Dynamic Percentages
 */
function updateDynamicDiscountBadges(discoveredPlans) {
  const subscriptionDiscountBadge = document.getElementById('subscriptionDiscountBadge');
  
  // Get active quantity for discount calculation
  const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
  const quantity = activeQuantity ? activeQuantity.dataset.quantity : '3';
  
  const plans = discoveredPlans.plansByQuantity[quantity];
  if (plans && plans.length > 0 && subscriptionDiscountBadge) {
    const plan = plans[0];
    if (plan.discountPercentage > 0) {
      subscriptionDiscountBadge.textContent = `${plan.discountPercentage}% off`;
      subscriptionDiscountBadge.style.display = '';
    } else {
      subscriptionDiscountBadge.style.display = 'none';
    }
  }
}

/**
 * Dispatch Variant Change Events to Skio (Enhanced with Dynamic Plans)
 */
function dispatchVariantChangeToSkio(quantity) {
  const skioComponent = document.querySelector('skio-plan-picker');
  if (!skioComponent) return;
  
  try {
    const productData = JSON.parse(skioComponent.getAttribute('product'));
    
    // Use dynamic plan discovery for more accurate variant matching
    const selectedVariant = productData.variants.find(variant => {
      const detectedQty = detectQuantityFromVariant(variant);
      return detectedQty === quantity;
    });
    
    if (selectedVariant) {
      document.dispatchEvent(new CustomEvent('variantChanged', {
        detail: { variantId: selectedVariant.id }
      }));
      
      skioComponent.setAttribute('selectedVariant', JSON.stringify(selectedVariant));
      
      if (skioComponent.updateSelectedVariant) {
        skioComponent.updateSelectedVariant(selectedVariant);
      }

      // Update pricing for the new selection
      if (window.SkioPlansCache.initialized) {
        updateDynamicPricingForQuantity(quantity);
      }
    }
  } catch (error) {
    console.error('Error dispatching variant change to Skio:', error);
  }
}

/**
 * Update Pricing for Selected Quantity
 */
function updateDynamicPricingForQuantity(quantity) {
  const plans = window.SkioPlansCache.plansByQuantity[quantity];
  if (!plans || plans.length === 0) return;

  const plan = plans[0];
  const subscriptionCurrentPrice = document.querySelector('#subscriptionCurrentPrice');
  const subscriptionOriginalPrice = document.querySelector('#subscriptionOriginalPrice');

  if (subscriptionCurrentPrice) {
    subscriptionCurrentPrice.textContent = formatPriceCents(plan.finalPrice);
  }

  if (subscriptionOriginalPrice) {
    subscriptionOriginalPrice.textContent = formatPriceCents(plan.compareAtPrice);
  }

  // Update discount badge
  const subscriptionDiscountBadge = document.getElementById('subscriptionDiscountBadge');
  if (subscriptionDiscountBadge && plan.discountPercentage > 0) {
    subscriptionDiscountBadge.textContent = `${plan.discountPercentage}% off`;
    subscriptionDiscountBadge.style.display = '';
  }
}

/**
 * Format Price from Cents (with currency detection)
 */
function formatPriceCents(priceInCents) {
  const currencyCode = getCurrencyCode();
  return (priceInCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Get Currency Code from price data or default to USD
 */
function getCurrencyCode() {
  const priceDataElement = document.getElementById('pdp-price-data');
  if (!priceDataElement) return 'USD';
  return priceDataElement.dataset.currencyCode || 'USD';
}

/**
 * Get Dynamic Plan Data for Quantity (Public API)
 */
function getDynamicPlanForQuantity(quantity) {
  if (!window.SkioPlansCache.initialized) return null;
  const plans = window.SkioPlansCache.plansByQuantity[quantity];
  return plans && plans.length > 0 ? plans[0] : null;
}

/**
 * Debug Helper: Verify Dynamic Plan System
 */
function verifyDynamicPlanSystem() {
  console.group('🔍 Dynamic Skio Plan System Verification');
  
  if (!window.SkioPlansCache.initialized) {
    console.error('❌ Dynamic plans not initialized');
    console.groupEnd();
    return false;
  }

  console.log('✅ Dynamic plans initialized');
  console.log('📊 Available plans by quantity:', window.SkioPlansCache.plansByQuantity);
  
  // Test each quantity
  ['1', '3', '6'].forEach(qty => {
    const plan = getDynamicPlanForQuantity(qty);
    if (plan) {
      console.log(`✅ Quantity ${qty}: ${plan.name} - ${formatPriceCents(plan.finalPrice)} (${plan.discountPercentage}% off)`);
    } else {
      console.log(`⚠️ Quantity ${qty}: No plan found`);
    }
  });

  // Verify dropdown sync
  const dropdownOptions = document.querySelectorAll('.pdp-details__delivery-option');
  console.log(`🎛️ Dropdown options: ${dropdownOptions.length}`);
  
  // Verify buy box sync
  const activeQuantity = document.querySelector('.pdp-details__selection-option--active[data-quantity]');
  console.log(`🛒 Active quantity: ${activeQuantity ? activeQuantity.dataset.quantity : 'none'}`);

  console.groupEnd();
  return true;
}

// Export functions for use in other modules
if (typeof window !== 'undefined') {
  window.dispatchVariantChangeToSkio = dispatchVariantChangeToSkio;
  window.getDynamicPlanForQuantity = getDynamicPlanForQuantity;
  window.initializeSkioDynamicPlans = initializeSkioDynamicPlans;
  window.verifyDynamicPlanSystem = verifyDynamicPlanSystem;
}

